import { NOTIFY_LOCALES, type NotifyLocale } from '@capsico/contracts';
import { deviceId } from '../api/device';
import { registerPushDevice, removePushDevice } from '../api/notify';
import { getLocale } from '../i18n';
import { obtainPushToken } from '../native/push';

/**
 * This phone's push registration (W18). The FCM token is remembered on the phone only so it can be
 * removed from the account on sign-out; it is not a password and gives no access to the account.
 */
const KEY = 'capsico:customer:push-token';

function remembered(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function remember(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(KEY, token);
    else window.localStorage.removeItem(KEY);
  } catch {
    // Storage blocked: the server still purges stale tokens when FCM rejects them.
  }
}

function pushLocale(): NotifyLocale {
  const locale = getLocale();
  return (NOTIFY_LOCALES as readonly string[]).includes(locale) ? (locale as NotifyLocale) : 'en';
}

/** Registers this phone for push after sign-in. Safe to call often; failures are silent. */
export async function registerThisDevice(): Promise<boolean> {
  const push = await obtainPushToken();
  if (!push) return false;
  try {
    await registerPushDevice({
      token: push.token,
      platform: push.platform,
      deviceId: deviceId(),
      locale: pushLocale(),
    });
    remember(push.token);
    return true;
  } catch {
    return false;
  }
}

/** Removes this phone's token from the account. Called before sign-out; never blocks it. */
export async function forgetThisDevice(): Promise<void> {
  const token = remembered();
  if (!token) return;
  try {
    await removePushDevice(token);
  } catch {
    // Signing out must work even when this fails.
  } finally {
    remember(null);
  }
}
